import { motion } from 'framer-motion';
import { Award, Users, TrendingUp, Shield, Building2, CheckCircle, Phone } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import LeadForm from '@/components/LeadForm';

const About = () => {
  return (
    <div className="min-h-screen bg-dark" data-testid="about-page">
      <Header />

      {/* Hero */}
      <section className="relative min-h-[500px] flex items-center justify-center overflow-hidden mt-32 bg-gradient-to-br from-dark via-dark-light to-dark">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-rose-gold/20 to-transparent"></div>
        </div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-rose-gold text-sm mb-6 uppercase tracking-widest font-semibold">Our Story</p>
            <h1 className="font-heading text-6xl md:text-8xl font-bold text-white mb-6 leading-tight">
              About <span className="text-rose-gold">PropVanta</span>
            </h1>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              Your trusted partner for premium real estate solutions in Mumbai
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-dark">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-rose-gold font-semibold text-sm mb-4 uppercase tracking-widest">Our Mission</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Building Trust, One Property at a Time
            </h2>
          </motion.div>

          <div className="space-y-6 text-white/70 text-lg leading-relaxed">
            <p>
              PropVanta Property Consultants was founded with a simple mission: to make premium real estate accessible to everyone. 
              As a trusted channel partner, we bridge the gap between leading developers and home seekers, 
              ensuring transparent, hassle-free property transactions.
            </p>
            <p>
              With years of experience in the Mumbai real estate market, we've helped hundreds of families 
              find their dream homes and investors secure profitable properties. Our commitment to excellence 
              and customer satisfaction sets us apart in the industry.
            </p>
            <p>
              Today, we're proud to be official partners with India's most prestigious builders including 
              Lodha, Rustomjee, Oberoi, Godrej and many more. Every property we showcase is verified, every price is transparent, 
              and every client receives personalized attention from our expert team.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-gradient-to-r from-rose-gold/10 to-rose-dark/10 border-y border-rose-gold/20">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="font-heading text-5xl font-bold text-rose-gold mb-2">500+</p>
              <p className="text-white/60 font-semibold">Happy Families</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <p className="font-heading text-5xl font-bold text-rose-gold mb-2">50+</p>
              <p className="text-white/60 font-semibold">Premium Projects</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <p className="font-heading text-5xl font-bold text-rose-gold mb-2">15+</p>
              <p className="text-white/60 font-semibold">Builder Partners</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <p className="font-heading text-5xl font-bold text-rose-gold mb-2">98%</p>
              <p className="text-white/60 font-semibold">Client Satisfaction</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-dark">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-rose-gold font-semibold text-sm mb-4 uppercase tracking-widest">Core Values</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white">
            What Drives Us
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            {
              icon: Shield,
              title: 'Trust & Transparency',
              description: 'Honest dealings with complete price transparency and verified listings.',
            },
            {
              icon: Award,
              title: 'Excellence',
              description: 'Committed to delivering the highest quality service to every client.',
            },
            {
              icon: Users,
              title: 'Client First',
              description: 'Your needs and satisfaction are our top priority in everything we do.',
            },
            {
              icon: Building2,
              title: 'Expert Guidance',
              description: 'Professional consultation from experienced real estate specialists.',
            },
          ].map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center p-8 glass-effect rounded-2xl border border-dark-lighter hover:border-rose-gold/50 transition-all"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-rose-gold to-rose-dark rounded-full mb-6 shadow-rose-glow">
                <value.icon className="text-white" size={32} />
              </div>
              <h3 className="font-heading text-xl font-semibold text-white mb-3">{value.title}</h3>
              <p className="text-white/60 leading-relaxed">{value.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gradient-to-b from-dark-light to-dark">
        <div className="px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-rose-gold font-semibold text-sm mb-4 uppercase tracking-widest">The PropVanta Advantage</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white">
              Why Choose PropVanta
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
            {[
              'Official channel partner with leading builders',
              '100% verified and authentic property listings',
              'Expert consultation and property guidance',
              'Free site visits to all projects',
              'Complete documentation and legal support',
              'Best market prices and negotiation',
              'Post-purchase support and assistance',
              'Transparent dealings with no hidden charges',
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-start gap-3 p-4 glass-effect rounded-xl border border-dark-lighter"
              >
                <CheckCircle className="text-rose-gold flex-shrink-0 mt-1" size={20} />
                <span className="text-white/80 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <a
              href="tel:+917020970161"
              className="btn-primary inline-flex items-center gap-3 text-sm uppercase tracking-wider"
            >
              <Phone size={20} />
              Call Us Now
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-dark">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-rose-gold font-semibold text-sm mb-4 uppercase tracking-widest">Get In Touch</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Find Your <span className="text-rose-gold">Dream Property?</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              Our expert team is here to guide you through every step of your property buying journey. 
              Get personalized recommendations, free site visits, and the best deals from premium builders.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="text-rose-gold" size={20} />
                <span className="text-white/80">Personalized property recommendations</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="text-rose-gold" size={20} />
                <span className="text-white/80">Free site visits with expert guidance</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="text-rose-gold" size={20} />
                <span className="text-white/80">Best prices guaranteed</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <LeadForm title="Get Free Consultation" />
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
